import { ref } from 'vue'
import apiInstance from '@/utils/api'

// ===== RECEPTION ACCOUNTS INTERFACES =====
export interface ReceptionAccount {
  id: string | number
  email?: string
  accountName?: string
  password?: string
  soundFile?: string
  soundFileName?: string
  soundFileMediaId?: string | number
  status?: 'active' | 'inactive' | 0 | 1
  restaurantId?: string
  rcp_auto_login?: string
  createdAt?: string
  updatedAt?: string
  [key: string]: any
}


// ===== COMPOSABLE FUNCTION =====
export const useReceptionAccounts = (getRestaurantId: () => string) => {
  // State
  const receptionAccounts = ref<ReceptionAccount[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Fetch reception accounts for a restaurant
  const loadReceptionAccounts = async (restaurantId: string): Promise<void> => {
    loading.value = true
    error.value = null
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/reception`)
      const data = response.data

      if (data.error === false || response.status === 200) {
        receptionAccounts.value = data.data || []
      } else {
        console.warn('⚠��� Could not load reception accounts')
        receptionAccounts.value = []
      }
    } catch (e) {
      console.warn('⚠️ Error loading reception accounts:', (e as Error).message)
      error.value = (e as Error).message
      receptionAccounts.value = []
    } finally {
      loading.value = false
    }
  }

  // Get the next suggested account name (<restaurant_domain>-rcp-<n>) from the API
  const suggestAccountName = async (restaurantId: string): Promise<string | null> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/reception/suggest-account-name`)
      const data = response.data

      if (data.error === false && data.data?.account_name) {
        return data.data.account_name as string
      }

      return null
    } catch (e: any) {
      console.warn('⚠️ Could not suggest reception account name:', e.message)
      return null
    }
  }

  // Create a new reception account
  const createAccount = async (restaurantId: string, accountData: any): Promise<{ account: ReceptionAccount | null; errors: any }> => {
    try {
      // Map form fields to API fields
      const payload: any = {
        email: accountData.accountName || accountData.email,
        password: accountData.password,
        status: accountData.status === 'active' ? 1 : (accountData.status === 1 ? 1 : 0),
      }

      // Add media IDs if available
      if (accountData.soundFileMediaId) {
        payload.media_ids = [accountData.soundFileMediaId]
      }

      const response = await apiInstance.post(`/restaurant/${restaurantId}/reception`, payload)
      const data = response.data

      if (data.error === false && response.status === 200) {
        const newAccount = data.data
        // Map response to our account structure
        const mappedAccount: ReceptionAccount = {
          id: newAccount.id,
          email: newAccount.email,
          accountName: newAccount.email,
          password: accountData.password,
          soundFile: newAccount.media?.[0]?.original_url || '',
          soundFileName: newAccount.media?.[0]?.original_url?.split('/')?.pop() || '',
          soundFileMediaId: newAccount.media?.[0]?.id,
          status: newAccount.status,
          restaurantId: restaurantId,
          rcp_auto_login: newAccount.rcp_auto_login || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        receptionAccounts.value.push(mappedAccount)
        return { account: mappedAccount, errors: null }
      } else if (data.error === true && data.errors) {
        console.warn('⚠️ Validation errors:', data.errors)
        return { account: null, errors: data.errors }
      } else {
        console.warn('⚠️ Could not create reception account')
        return { account: null, errors: null }
      }
    } catch (e: any) {
      console.error('Error creating reception account:', e.message)
      // Check if it's a validation error response
      if (e.response?.data?.errors) {
        return { account: null, errors: e.response.data.errors }
      }
      return { account: null, errors: null }
    }
  }

  // Update a reception account
  const updateAccount = async (restaurantId: string, accountId: string | number, updates: any): Promise<{ account: ReceptionAccount | null; errors: any }> => {
    try {
      // Map form fields to API fields for update
      // Note: email is NOT included in PUT request (account email cannot be changed)
      const payload: any = {
        status: updates.status === 'active' ? 1 : (updates.status === 1 ? 1 : 0),
      }

      // Add password only if provided (optional on edit)
      if (updates.password && updates.password.trim()) {
        payload.password = updates.password
      }

      // Add media IDs if available
      if (updates.soundFileMediaId) {
        payload.media_ids = [updates.soundFileMediaId]
      }

      const response = await apiInstance.put(`/restaurant/${restaurantId}/reception/${accountId}`, payload)
      const data = response.data

      if (data.error === false && response.status === 200) {
        const updatedAccount = data.data
        // Map response to our account structure
        const mappedAccount: ReceptionAccount = {
          id: updatedAccount.id,
          email: updatedAccount.email,
          accountName: updatedAccount.email,
          password: updates.password || '',
          soundFile: updatedAccount.media?.[0]?.original_url || '',
          soundFileName: updatedAccount.media?.[0]?.original_url?.split('/')?.pop() || '',
          soundFileMediaId: updatedAccount.media?.[0]?.id,
          status: updatedAccount.status,
          restaurantId: restaurantId,
          rcp_auto_login: updatedAccount.rcp_auto_login || '',
          createdAt: updatedAccount.createdAt || new Date().toISOString(),
          updatedAt: updatedAccount.updatedAt || new Date().toISOString(),
        }
        const index = receptionAccounts.value.findIndex((a) => String(a.id) === String(accountId))
        if (index !== -1) {
          receptionAccounts.value[index] = mappedAccount
        }
        return { account: mappedAccount, errors: null }
      } else if (data.error === true && data.errors) {
        console.warn('⚠️ Validation errors:', data.errors)
        return { account: null, errors: data.errors }
      } else {
        console.warn('⚠️ Could not update reception account')
        return { account: null, errors: null }
      }
    } catch (e: any) {
      console.error('Error updating reception account:', e.message)
      // Check if it's a validation error response
      if (e.response?.data?.errors) {
        return { account: null, errors: e.response.data.errors }
      }
      return { account: null, errors: null }
    }
  }

  // Delete a reception account
  const deleteAccount = async (restaurantId: string, accountId: string | number): Promise<boolean> => {
    try {
      const response = await apiInstance.delete(`/restaurant/${restaurantId}/reception/${accountId}`)
      const data = response.data

      if (data.error === false || response.status === 200) {
        receptionAccounts.value = receptionAccounts.value.filter((a) => String(a.id) !== String(accountId))
        return true
      } else {
        console.warn('⚠️ Could not delete reception account')
        return false
      }
    } catch (e) {
      console.error('Error deleting reception account:', (e as Error).message)
      return false
    }
  }

  // Get account details
  const getAccountDetails = async (restaurantId: string, accountId: string | number): Promise<ReceptionAccount | null> => {
    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/reception/${accountId}`)
      const data = response.data

      if (data.error === false && data.data) {
        const accountData = data.data
        const mappedAccount: ReceptionAccount = {
          id: accountData.id,
          email: accountData.email,
          accountName: accountData.email,
          password: '',
          soundFile: accountData.media?.[0]?.original_url || '',
          soundFileName: accountData.media?.[0]?.original_url?.split('/')?.pop() || '',
          soundFileMediaId: accountData.media?.[0]?.id,
          status: accountData.status,
          restaurantId: restaurantId,
          rcp_auto_login: accountData.rcp_auto_login || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        return mappedAccount
      }

      console.warn('⚠️ Could not fetch account details')
      return null
    } catch (e: any) {
      console.error('Error fetching account details:', e.message)
      return null
    }
  }

  // Toggle account status
  const toggleAccountStatus = async (restaurantId: string, accountId: string | number): Promise<ReceptionAccount | null> => {
    try {
      const account = receptionAccounts.value.find((a) => String(a.id) === String(accountId))
      if (!account) return null

      const newStatus = account.status === 1 || account.status === 'active' ? 0 : 1
      const response = await apiInstance.put(`/restaurant/${restaurantId}/reception/${accountId}`, { status: newStatus })
      const data = response.data

      if (data.error === false || response.status === 200) {
        const updatedAccount = data.data
        const index = receptionAccounts.value.findIndex((a) => String(a.id) === String(accountId))
        if (index !== -1) {
          receptionAccounts.value[index] = updatedAccount
        }
        return updatedAccount
      } else {
        console.warn('⚠️ Could not toggle reception account status')
        return null
      }
    } catch (e) {
      console.error('Error toggling reception account status:', (e as Error).message)
      return null
    }
  }

  // Get account by ID
  const getAccountById = (accountId: string | number): ReceptionAccount | undefined => {
    return receptionAccounts.value.find((a) => String(a.id) === String(accountId))
  }

  // Get restaurant accounts
  const getRestaurantAccounts = (restaurantId: string): ReceptionAccount[] => {
    return receptionAccounts.value.filter((account) => account.restaurantId === restaurantId)
  }

  const uploadMedia = async (file: File, typeId?: string | number): Promise<{ mediaId: string | number; mediaUrl: string } | null> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for upload')
      return null
    }

    try {
      const formData = new FormData()
      formData.append('type', 'reception')
      if (typeId) {
        formData.append('type_id', String(typeId))
      }
      formData.append('media[]', file)

      const response = await apiInstance.post(`/restaurant/${restaurantId}/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const data = response.data

      if (data.error === false && data.data) {
        // Extract media ID from media_ids array
        let mediaId: string | number | null = null
        let mediaUrl: string | null = null

        // Handle the new response format where media_ids contains objects
        if (data.data.media_ids && Array.isArray(data.data.media_ids) && data.data.media_ids.length > 0) {
          const mediaItem = data.data.media_ids[0] // Assuming only one media item is uploaded at a time
          if (typeof mediaItem === 'object' && mediaItem.id) {
            // New format: {id, mime_type, original_url}
            mediaId = mediaItem.id
            mediaUrl = mediaItem.original_url
          } else {
            // Fallback for older format where it's just the ID
            mediaId = mediaItem
          }
        } else if (data.data.id) {
          mediaId = data.data.id
        } else if (data.data.media_id) {
          mediaId = data.data.media_id
        }

        if (mediaId) {
          const result = {
            mediaId: mediaId,
            mediaUrl: mediaUrl || file.name, // Use original_url if available, else fallback to file name
          }
          return result
        }
      }

      console.warn('⚠️ Upload response indicates failure or missing media_ids:', data)
      return null
    } catch (error) {
      console.error('🔴 Error uploading media:', error)
      return null
    }
  }

  const deleteMedia = async (mediaId: string | number, typeId?: string | number): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for media deletion')
      return false
    }

    if (!mediaId) {
      console.error('🔴 Media ID is not available for deletion')
      return false
    }

    try {
      const payload: any = {
        type: 'reception',
      }

      if (typeId) {
        payload.type_id = String(typeId)
      }

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/delete-media/${mediaId}`, {
        data: payload,
      })

      const data = response.data

      if (data.error === false || response.status === 200 || response.status === 204) {
        return true
      }

      console.warn('���️ Delete response indicates failure:', data)
      return false
    } catch (error) {
      console.error('🔴 Error deleting media:', error)
      return false
    }
  }

  // Generate a temporary (10-minute, single-use) auto-login link for an account
  const generateLoginToken = async (
    restaurantId: string,
    accountId: string | number
  ): Promise<{ success: boolean; loginUrl: string | null; expiresAt: string | null; message: string | null }> => {
    try {
      const response = await apiInstance.post(`/restaurant/${restaurantId}/reception/${accountId}/generate-login-token`)
      const data = response.data

      if (data.error === true || !data.data?.login_url) {
        return { success: false, loginUrl: null, expiresAt: null, message: data.message || 'Failed to generate login link' }
      }

      return { success: true, loginUrl: data.data.login_url as string, expiresAt: data.data.expires_at as string, message: null }
    } catch (e: any) {
      console.error('Error generating reception login token:', e.message)
      return { success: false, loginUrl: null, expiresAt: null, message: e.response?.data?.message || 'Network error.' }
    }
  }

  return {
    receptionAccounts,
    loading,
    error,
    loadReceptionAccounts,
    suggestAccountName,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleAccountStatus,
    getAccountById,
    getRestaurantAccounts,
    getAccountDetails,
    uploadMedia,
    deleteMedia,
    generateLoginToken,
  }
}
